import { HashRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PrivateRoute } from './components/PrivateRoute';
import { AdminRoute } from './components/AdminRoute';
import Header from './components/Header';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import ShareConfirmation from './pages/ShareConfirmation';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignUpFlow from './pages/SignUpFlow';
import ForgotPassword from './pages/ForgotPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SingleSearch from './pages/SingleSearch';
import MyCrush from './pages/MyCrush';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import AdminUsers from './pages/AdminUsers';
import AdminAnnonces from './pages/AdminAnnonces';
import PromoteFirstAdmin from './pages/PromoteFirstAdmin';
import OfflineAlert from './components/OfflineAlert';
import { useEffect } from 'react';
import { PushNotificationService } from './services/pushNotificationService';
import { InstallPWA } from './components/InstallPWA';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Heart, User } from 'lucide-react';
import { useAuthContext } from './contexts/AuthContext';
import { theme } from './theme';

// Composants d'icônes avec dégradé
const HomeIconGradient = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#homeGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="homeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const HeartIconGradient = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#heartGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="heartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const UserIconGradient = ({ className = "" }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="url(#userGradient)"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <defs>
      <linearGradient id="userGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor={theme.colors.primary.start} />
        <stop offset="100%" stopColor={theme.colors.primary.end} />
      </linearGradient>
    </defs>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Composant de la barre de navigation en bas
function BottomNavbar() {
  const location = useLocation();
  const { user } = useAuthContext();
  
  // Vérifier si nous sommes sur une page où la barre ne devrait pas s'afficher
  const hideNavbar = 
    location.pathname.startsWith('/admin') || 
    location.pathname.startsWith('/share/') ||
    location.pathname === '/settings' ||
    location.pathname === '/signup-flow' ||
    location.pathname === '/promote-first-admin' ||
    ['/login', '/signup', '/forgot-password', '/privacy-policy'].includes(location.pathname);
    
  // Vérifier si un modal est ouvert sur la page Profile
  const urlParams = new URLSearchParams(location.search);
  const isAddRelationModalOpen = location.pathname === '/profile' && urlParams.get('show_modal') === 'add_relation';
  
  // Vérifier si le modal "voir tout" est ouvert sur la page Home
  const isAllResultsModalOpen = location.pathname === '/' && urlParams.get('show_modal') === 'all_results';
  
  if (hideNavbar || isAddRelationModalOpen || isAllResultsModalOpen) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 py-2 z-50">
      <div className="container mx-auto flex justify-around items-center">
        <Link to="/" className="flex items-center p-2">
          {location.pathname === '/' ? (
            <HomeIconGradient className="w-6 h-6" />
          ) : (
            <HomeIcon className="w-6 h-6 text-gray-400" />
          )}
        </Link>
        
        <Link to="/single-search" className="flex items-center p-2">
          {location.pathname === '/single-search' ? (
            <HeartIconGradient className="w-6 h-6" />
          ) : (
            <Heart className="w-6 h-6 text-gray-400" />
          )}
        </Link>
        
        <Link to={user ? "/profile" : "/login"} className="flex items-center p-2">
          {location.pathname === '/profile' ? (
            <UserIconGradient className="w-6 h-6" />
          ) : (
            <User className="w-6 h-6 text-gray-400" />
          )}
        </Link>
      </div>
    </div>
  );
}

function AppContent() {
  const location = useLocation();
  
  // Gérer les paramètres d'authentification dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('error')) {
      console.error('Erreur d\'authentification:', params.get('error'));
    }
  }, [location]);

  const isAdminPage = location.pathname.startsWith('/admin');
  
  const showHeader = !isAdminPage && 
                    location.pathname !== '/profile' && 
                    !location.pathname.startsWith('/share/') &&
                    location.pathname !== '/settings' &&
                    location.pathname !== '/single-search' &&
                    location.pathname !== '/my-crush' &&
                    location.pathname !== '/promote-first-admin' &&
                    location.pathname !== '/signup-flow' &&
                    !['/login', '/signup', '/forgot-password', '/privacy-policy'].includes(location.pathname);

  // Déterminer si la barre de navigation du bas est visible
  const showBottomNav = !isAdminPage && 
                      !location.pathname.startsWith('/share/') &&
                      location.pathname !== '/settings' &&
                      location.pathname !== '/signup-flow' &&
                      location.pathname !== '/promote-first-admin' &&
                      !['/login', '/signup', '/forgot-password', '/privacy-policy'].includes(location.pathname);

  return (
    <div className="min-h-screen bg-black text-white">
      <OfflineAlert />
      {showHeader && <Header />}
      <main className={`container mx-auto ${showHeader && !isAdminPage ? 'px-4 py-8' : ''} ${showBottomNav ? 'pb-20' : ''}`} role="main">
        <Routes>
          {/* Routes publiques */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/share/:relationId" element={<ShareConfirmation />} />
          <Route path="/promote-first-admin" element={<PromoteFirstAdmin />} />
          
          {/* Routes privées */}
          <Route path="/" element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          } />
          <Route path="/single-search" element={
            <PrivateRoute>
              <SingleSearch />
            </PrivateRoute>
          } />
          <Route path="/signup-flow" element={
            <PrivateRoute>
              <SignUpFlow />
            </PrivateRoute>
          } />
          <Route path="/profile" element={
            <PrivateRoute>
              <Profile />
            </PrivateRoute>
          } />
          <Route path="/settings" element={
            <PrivateRoute>
              <Settings />
            </PrivateRoute>
          } />
          <Route path="/my-crush" element={
            <PrivateRoute>
              <MyCrush />
            </PrivateRoute>
          } />
          
          {/* Routes administratives */}
          <Route path="/admin" element={
            <PrivateRoute>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/referrals" element={
            <PrivateRoute>
              <AdminRoute requiredPermission="users.view">
                <AdminDashboard />
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/users" element={
            <PrivateRoute>
              <AdminRoute requiredPermission="users.view">
                <AdminUsers />
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/relations" element={
            <PrivateRoute>
              <AdminRoute requiredPermission="relations.view">
                <AdminDashboard />
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/payments" element={
            <PrivateRoute>
              <AdminRoute requiredPermission="payments.view">
                <AdminDashboard />
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/settings" element={
            <PrivateRoute>
              <AdminRoute requiredPermission="settings.view">
                <AdminSettings />
              </AdminRoute>
            </PrivateRoute>
          } />
          <Route path="/admin/annonces" element={
            <PrivateRoute>
              <AdminRoute>
                <AdminAnnonces />
              </AdminRoute>
            </PrivateRoute>
          } />
          
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </main>
      <BottomNavbar />
    </div>
  );
}

function App() {
  useEffect(() => {
    const initializePushNotifications = async () => {
      try {
        // Vérifier si nous avons déjà demandé la permission
        if ('Notification' in window && Notification.permission === 'granted') {
          console.log('Permission pour les notifications déjà accordée');
          
          // Vérifier si nous sommes déjà abonnés
          const registration = await navigator.serviceWorker.ready;
          const existingSubscription = await registration.pushManager.getSubscription();
          
          if (existingSubscription) {
            console.log('Déjà abonné aux notifications push');
            return;
          }
        }
        
        // Sinon, demander la permission et s'abonner
        const permissionGranted = await PushNotificationService.requestPermission();
        
        if (permissionGranted) {
          const subscription = await PushNotificationService.subscribeToPush();
          if (subscription) {
            console.log('Abonnement aux notifications push réussi');
          }
        }
      } catch (error) {
        console.error('Erreur lors de l\'initialisation des notifications push:', error);
      }
    };

    initializePushNotifications();
  }, []);

  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </AuthProvider>
      <InstallPWA />
    </Router>
  );
}

export default App;